import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SEOHead } from './SEOHead';

const DEFAULT_BASE_URL = 'https://starteaseai.com';
const SITE_NAME = 'StartEase Agent';

/**
 * StatePageSEO - SEO component for state landing pages
 * @param {Object} props
 * @param {Object} props.state - State page data from API
 * @param {string} props.state.state_name - Full state name
 * @param {string} props.state.state_code - State abbreviation (e.g., 'CA')
 * @param {string} props.state.slug - State URL slug
 * @param {string} [props.state.page_heading] - Page heading
 * @param {string} [props.state.intro_paragraph] - Intro paragraph
 * @param {number} [props.state.govt_fee_llc] - LLC government fee
 * @param {number} [props.state.processing_time_days] - Processing time in days
 * @param {string} [props.state.meta_title] - SEO meta title
 * @param {string} [props.state.meta_description] - SEO meta description
 * @param {string} [props.state.og_title] - Open Graph title
 * @param {string} [props.state.og_description] - Open Graph description
 * @param {string} [props.state.og_image] - Open Graph image
 * @param {string} [props.state.canonical_url] - Canonical URL
 * @param {string} [props.state.schema_type] - JSON-LD schema type
 * @param {string[]} [props.state.focus_keywords] - Focus keywords
 * @param {boolean} [props.state.is_published] - Publication status
 * @param {string} [props.baseUrl] - Base URL for the site
 */
export const StatePageSEO = ({
  state,
  baseUrl = DEFAULT_BASE_URL,
}) => {
  const title = state.meta_title || `Start a Business in ${state.state_name}`;
  const description = state.meta_description || 
    `Form your LLC or Corporation in ${state.state_name}. Professional business formation services with fast processing and expert support.`;
  const canonical = state.canonical_url || `${baseUrl}/states/${state.slug}`;
  const keywords = state.focus_keywords?.join(', ');
  const noindex = state.is_published === false;

  // Service JSON-LD Schema with areaServed
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': state.schema_type || 'Service',
    name: `Business Formation in ${state.state_name}`,
    description: state.intro_paragraph || description,
    url: canonical,
    provider: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: DEFAULT_BASE_URL,
    },
    areaServed: {
      '@type': 'State',
      name: state.state_name,
      addressCountry: 'US',
    },
    ...(state.govt_fee_llc && {
      offers: {
        '@type': 'Offer',
        price: state.govt_fee_llc,
        priceCurrency: 'USD',
        description: 'State filing fee for LLC formation',
        availability: 'https://schema.org/InStock',
      },
    }),
  };

  // BreadcrumbList JSON-LD Schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: DEFAULT_BASE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'States',
        item: `${DEFAULT_BASE_URL}/states`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: state.state_name,
        item: canonical,
      },
    ],
  };

  // LocalBusiness JSON-LD for local SEO
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: `${SITE_NAME} - ${state.state_name}`,
    description: `Business formation services in ${state.state_name}`,
    url: canonical,
    address: {
      '@type': 'PostalAddress',
      addressRegion: state.state_code,
      addressCountry: 'US',
    },
    areaServed: {
      '@type': 'State',
      name: state.state_name,
    },
  };

  return (
    <>
      <SEOHead
        title={title}
        description={description}
        canonical={canonical}
        ogTitle={state.og_title}
        ogDescription={state.og_description}
        ogImage={state.og_image}
        ogType="website"
        keywords={keywords}
        noindex={noindex}
      />
      <Helmet>
        {/* Geo meta tags for local SEO */}
        <meta name="geo.region" content={`US-${state.state_code}`} />
        <meta name="geo.placename" content={state.state_name} />
        
        {/* JSON-LD Schemas */}
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      </Helmet>
    </>
  );
};

export default StatePageSEO;
